import { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, AlertTriangle, MapPin, Loader2 } from "lucide-react";

const PARTS = [["street_address", "Street address", "1234 Example St", "sm:col-span-2"], ["city", "City", "Las Vegas", ""],
  ["state", "State", "Nevada", ""], ["zip_code", "ZIP code", "89109", ""], ["country", "Country", "United States", ""]];

function useSuggestions(q, active) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!active || q.trim().length < 3) { setItems([]); return undefined; }
    setBusy(true);
    const t = setTimeout(() => api.get("/address/suggest", { params: { q } }).then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setBusy(false)), 300);
    return () => clearTimeout(t);
  }, [q, active]);
  return { items, busy, clear: () => setItems([]) };
}

function ZoneHint({ z }) {
  if (!z) return null;
  const ok = z.status === "verified";
  return (
    <p className={`flex items-start gap-1 text-xs ${ok ? "text-emerald-700" : "text-amber-700"}`} data-testid="address-zone-hint">
      {ok ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
      {ok ? `Tax area found on Clark County's official map: ${z.zone.replaceAll("_", " ")}` : `Tax area needs admin review: ${(z.flags || []).join("; ")}`}
    </p>
  );
}

// Required full property address with live suggestions; private unless the homeowner chooses to publish it.
export function AddressSection({ form }) {
  const { f, patch, set } = form;
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [zone, setZone] = useState(null);
  const [err, setErr] = useState("");
  const typed = useRef(false);
  const { items, busy, clear } = useSuggestions(q, open);
  const pick = async (s) => {
    setOpen(false); clear(); setErr("");
    try {
      const { data: a } = await api.get("/address/resolve", { params: { text: s.text, magic_key: s.magic_key } });
      if (!a.precise) { setErr("Pick a full street address with a house number"); return; }
      patch({ street_address: a.street_address, city: a.city, state: a.state, zip_code: a.zip_code, country: a.country === "USA" ? "United States" : a.country,
        county: a.county, location: `${a.city}, ${a.state}`, ...(a.tax_zone?.status === "verified" ? { jurisdiction: a.tax_zone.zone } : {}) });
      setZone(a.tax_zone);
      setQ(s.text);
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  return (
    <div className="space-y-4 rounded-2xl border p-5" data-testid="listing-address-section">
      <div><p className="font-semibold">Property address (required)</p>
        <p className="text-sm text-stone-600">Where the home actually is, not a mailing address. Guests see only the city and an approximate map area until their stay is paid.</p></div>
      <div className="relative">
        <Label htmlFor="address-search">Search address</Label>
        <div className="relative mt-2">
          <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input id="address-search" className="pl-9" autoComplete="off" placeholder="Start typing the street address" value={q}
            onChange={(e) => { typed.current = true; setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} data-testid="address-search-input" />
          {busy && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-stone-400" />}
        </div>
        {open && items.length > 0 && (
          <ul className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-xl border bg-white shadow-lg" data-testid="address-suggestions">
            {items.map((s, i) => (
              <li key={s.magic_key}><button type="button" className="w-full px-4 py-3 text-left text-sm hover:bg-stone-50" onClick={() => pick(s)} data-testid={`address-suggestion-${i}`}>{s.text}</button></li>
            ))}
          </ul>
        )}
      </div>
      {err && <p className="text-xs text-red-600" data-testid="address-error">{err}</p>}
      <ZoneHint z={zone} />
      <div className="grid gap-4 sm:grid-cols-2">
        {PARTS.map(([k, label, ph, span]) => (
          <div key={k} className={`space-y-2 ${span}`}><Label>{label}</Label>
            <Input value={f[k] || ""} onChange={set(k)} placeholder={ph} data-testid={`listing-${k.replace("_", "-")}-input`} /></div>
        ))}
      </div>
      <label className="flex items-start gap-2 text-sm" data-testid="listing-show-address-label">
        <input type="checkbox" className="mt-1" checked={!!f.show_full_address} onChange={(e) => patch({ show_full_address: e.target.checked })} data-testid="listing-show-address-checkbox" />
        <span>Show the full street address on my public listing. If you leave this off, guests get it once their stay is paid.</span>
      </label>
    </div>
  );
}
